import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Calendar, Shield, Laptop, CheckCircle, XCircle, Edit2, Save, X } from 'lucide-react';
import Header from './Header';
import { formatDate } from '../utils/dateFormatter';

// Using relative URLs

const EmployeeDetail = () => {
  const { employeeId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    role: '',
    status: ''
  });

  useEffect(() => {
    fetchEmployee();
  }, [employeeId]);

  const fetchEmployee = async () => {
    try {
      const response = await axios.get(`/api/employees/${employeeId}`);
      setEmployee(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        role: response.data.role || '',
        status: response.data.status || 'active'
      });
    } catch (error) {
      toast.error('Failed to load employee details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`/api/employees/${employeeId}`, formData);
      toast.success('Employee updated successfully!');
      setEditing(false);
      fetchEmployee();
    } catch (error) {
      toast.error('Failed to update employee');
      console.error(error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.post(`/api/employees/${employeeId}/mark-status`, null, {
        params: { status: newStatus }
      });
      toast.success(`Employee marked as ${newStatus}`);
      fetchEmployee();
    } catch (error) {
      toast.error('Failed to update status');
      console.error(error);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'admin':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'employee':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'auditor':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-100 text-emerald-800 border-emerald-300';
      case 'exited':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'non_employee':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Employee Not Found</h2>
            <Button onClick={() => navigate('/employees')}>
              Back to Employees
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="outline"
          onClick={() => navigate('/employees')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Employees
        </Button>

        {/* Employee Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <User className="w-6 h-6 mr-2 text-navy-600" />
                Employee Details
              </CardTitle>
              {!editing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {editing ? (
              // Edit Mode
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Full Name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Role</Label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full h-10 px-4 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500"
                  >
                    <option value="employee">Employee</option>
                    <option value="admin">Admin</option>
                    <option value="auditor">Auditor</option>
                    {user?.role === 'owner' && <option value="owner">Owner</option>}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full h-10 px-4 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500"
                  >
                    <option value="active">Active</option>
                    <option value="exited">Exited</option>
                    <option value="non_employee">Non-Employee</option>
                  </select>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleUpdate}
                    className="bg-navy-600 hover:bg-navy-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        full_name: employee.full_name || '',
                        role: employee.role || '',
                        status: employee.status || 'active'
                      });
                    }}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Full Name</p>
                  <p className="font-medium text-lg">{employee.full_name || 'Not set'}</p>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">Email</p>
                  <p className="font-medium text-lg flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-slate-400" />
                    {employee.email}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">Role</p>
                  <Badge className={getRoleBadgeColor(employee.role)}>
                    {employee.role}
                  </Badge>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">Status</p>
                  <Badge className={getStatusBadgeColor(employee.status || 'active')}>
                    {employee.status || 'active'}
                  </Badge>
                </div>

                <div>
                  <p className="text-sm text-slate-500 mb-1">Onboarding Date</p>
                  <p className="font-medium flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                    {employee.onboarding_date 
                      ? formatDate(employee.onboarding_date)
                      : 'N/A'}
                  </p>
                </div>

                {employee.exit_date && (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Exit Date</p>
                    <p className="font-medium flex items-center text-red-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      {formatDate(employee.exit_date)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Device Compliance Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Laptop className="w-6 h-6 mr-2 text-navy-600" />
              Device Compliance
            </CardTitle>
            <CardDescription>Security status of employee's device</CardDescription>
          </CardHeader>
          
          <CardContent>
            {employee.device_compliance ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Encryption Enabled</span>
                  {employee.device_compliance.encryption_enabled ? (
                    <CheckCircle className="w-5 h-5 text-navy-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Antivirus Installed</span>
                  {employee.device_compliance.antivirus_installed ? (
                    <CheckCircle className="w-5 h-5 text-navy-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Screen Lock Enabled</span>
                  {employee.device_compliance.screen_lock_enabled ? (
                    <CheckCircle className="w-5 h-5 text-navy-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Laptop className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No device compliance data available</p>
                <p className="text-sm mt-1">Device agent not installed or data not synced</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        {(employee.status === 'active' || employee.status === 'exited') && (
          <div className="mt-6 flex gap-4">
            {employee.status === 'active' && (
              <Button
                variant="outline"
                onClick={() => handleStatusChange('exited')}
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Mark as Exited
              </Button>
            )}
            {employee.status === 'exited' && (
              <Button
                variant="outline"
                onClick={() => handleStatusChange('active')}
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                Reactivate Employee
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDetail;
